import React, { PropsWithChildren } from "react";
//#region types
import { TableRowProps } from "models/propTypes/simpleTableProps";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import useValueFormatter from "./useValueFormatter";
import CheckBox from "../forms/baseFields/checkBox";
import Icon from "../icon";
import { Grid, IconButton, TableCell, TableRow, Tooltip, Skeleton } from "@mui/material";
import useMenuButtonClick from "../buttons/useMenuButtonClick";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { LocalizationHelperInstance } from "utils/helpers";

const TableContentRow: React.FC<PropsWithChildren<TableRowProps>> = (props) => {
  const {
    row,
    index,
    columns,
    allowCheckBoxSelection,
    disableCheckBoxSelection,
    toggleSelectionHandler,
    rowHoverActions,
  } = props;

  const onClickRow = (row: any, rowIndex: number) => {
    props.rowClickHandler && props.rowClickHandler(row, rowIndex);
  };

  const onCheckBoxClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const onHoverActionClick = useMenuButtonClick();

  const { getFormattedValue } = useValueFormatter();

  const additionalRowStyles = typeof props.rowClasses === "function" ? props.rowClasses(row) : props.rowClasses;

  return (
    <TableRow
      hover
      className={clsx(cssExports.tableRow, props.className)}
      classes={{ hover: cssExports.tableRowHover }}
      style={{
        cursor: props.rowClickHandler ? "pointer" : "auto",
      }}
      onClick={props.rowClickHandler ? onClickRow.bind(null, row, index) : () => {}}
    >
      {props.children}
      {allowCheckBoxSelection && (
        <TableCell
          key={`tab-headerTop`}
          className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.select)}
          data-testid={`checkBoxCell`}
        >
          {props.loading ? (
            <Skeleton width="100%" height="100%" />
          ) : (
            <Tooltip
              disableInteractive
              placement="top"
              title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DisableRowTooltip)}
              classes={{ tooltip: cssExports.tooltip, popper: cssExports.tooltipPopper }}
              disableHoverListener={!disableCheckBoxSelection}
            >
              <div>
                <CheckBox
                  checked={row.select ?? false}
                  className={disableCheckBoxSelection ? cssExports.disable : ""}
                  onChange={toggleSelectionHandler.bind(null, index)}
                  onClick={onCheckBoxClick}
                />
              </div>
            </Tooltip>
          )}
        </TableCell>
      )}
      {columns.map(([key, cellValue], cellIndex) => (
        <TableCell
          key={`tab-cell-${index}${index}${cellIndex}`}
          data-testid={`${key}Cell`}
          className={clsx(
            cssExports.tableCell,
            cellValue?.isTextSmall ? cssExports.textSmall : "",
            cssExports.truncateString,
            cellValue?.className,
            additionalRowStyles
          )}
          align={cellValue?.alignment}
          style={{
            whiteSpace: cellValue?.noWrap ? "nowrap" : "normal",
            width: cellValue?.width,
            maxWidth: cellValue?.maxWidth,
          }}
        >
          {props.loading ? (
            <Skeleton width="100%" height="100%" />
          ) : (
            getFormattedValue(cellValue?.formatter ? cellValue.formatter(row, index) : row[key])
          )}
        </TableCell>
      ))}
      {rowHoverActions && rowHoverActions.length > 0 ? (
        <TableCell className={cssExports.hoverActionButtons} data-testid={`hoverActionButtonCell`}>
          <Grid container direction="row" className={cssExports.hoverButtonsContainer}>
            {rowHoverActions.map((item, index) => {
              return (
                <Tooltip
                  key={index}
                  disableInteractive
                  placement="top"
                  title={item.description}
                  arrow
                  classes={{
                    tooltip: cssExports.tooltip,
                    popper: cssExports.tooltipPopper,
                    arrow: cssExports.tooltipArrow,
                  }}
                >
                  <Grid item className={cssExports.hoverActionButton} data-testid={`hoverActionButton`}>
                    <IconButton
                      className={clsx(cssExports.hoverActionButton)}
                      classes={{ root: cssExports.hoverButtonRoot }}
                      onClick={onHoverActionClick.bind(null, item, false)}
                    >
                      <Icon icon={item.icon} />
                    </IconButton>
                  </Grid>
                </Tooltip>
              );
            })}
          </Grid>
        </TableCell>
      ) : (
        <></>
      )}
    </TableRow>
  );
};

export default TableContentRow;
